import { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Mail,
  Phone,
  Send,
  Shield,
  ShieldCheck,
} from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { Grievances } from "@/lib/api";
import { useRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";

const CATEGORIES = [
  "Customer Service Issue",
  "Delay in Processing",
  "Documentation Issue",
  "Employee Conduct",
  "Advisor Conduct",
  "DSA Conduct",
  "Communication Issue",
  "Billing/Charges Issue",
  "Data Privacy Concern",
  "Other",
];

const ESCALATION = [
  { level: 1, role: "Customer Service Executive", contact: "First point of contact at any branch" },
  { level: 2, role: "Branch Manager", contact: "If unresolved within 3 working days" },
  { level: 3, role: "Area Manager", contact: "Escalate after 5 working days" },
  { level: 4, role: "District Head", contact: "Escalate after 7 working days" },
  { level: 5, role: "State Head", contact: "Escalate after 10 working days" },
  { level: 6, role: "Managing Director", contact: "sahyogpfir@gmail.com" },
  { level: 7, role: "Chairman", contact: "Final internal authority" },
];

const SLAS = [
  { k: "Same Day", v: "Complaint Registration" },
  { k: "24 Hours", v: "Acknowledgement to Customer" },
  { k: "48 Hours", v: "Investigation Initiation" },
  { k: "7 Days", v: "Resolution Attempt" },
  { k: "15 Days", v: "Final Closure" },
];

export default function Grievance() {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "", ticket: null });
  const getRecaptchaToken = useRecaptchaToken();

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "category", "details"];
    if (required.some((k) => !String(form[k] || "").trim())) {
      setStatus({ state: "error", message: "Please fill all required fields.", ticket: null });
      return;
    }
    setStatus({ state: "loading", message: "Submitting your complaint…", ticket: null });
    try {
      const recaptcha_token = await getRecaptchaToken("grievance");
      const data = await Grievances.create({ ...form, recaptcha_token });
      setStatus({
        state: "success",
        message: "Your complaint has been registered. Our team will acknowledge within 24 hours.",
        ticket: data.ticket_no,
      });
      setForm({});
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not submit. Please try again.",
        ticket: null,
      });
    }
  };

  return (
    <PolicyPageShell
      testId="grievance-page"
      eyebrow="Customer Grievance Management"
      title={
        <>
          Raise a <em className="not-italic text-[#D4AF37]">Complaint</em>,{" "}
          <span className="font-script italic text-[#F0D788]">Get Resolution</span>
        </>
      }
      lead="Every voice matters. Submit your grievance below — we acknowledge within 24 hours and aim to close every case within 15 working days, per our Customer Grievance Management Policy."
    >
      {/* SLA strip */}
      <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-5 gap-3 mb-14">
        {SLAS.map((s) => (
          <div
            key={s.v}
            data-testid={`grievance-sla-${s.k.replace(/\s+/g, "-").toLowerCase()}`}
            className="rounded-2xl p-4 border border-[#D4AF37]/25 bg-white/[0.03] text-center"
          >
            <Clock size={16} className="mx-auto text-[#D4AF37]" />
            <div className="font-display text-xl text-[#F0D788] mt-2">{s.k}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/55 mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Form */}
        <form
          data-testid="grievance-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-[#0A1F44] rounded-3xl p-6 md:p-10 border border-[#D4AF37]/25 shadow-2xl"
        >
          <h2 className="font-display text-2xl">Complaint Form (SF-NI-CGM-CCF-001)</h2>
          <p className="text-white/55 text-xs mt-1">Fields marked * are required.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *" k="name" form={form} onField={onField} />
            <Field label="Mobile Number *" k="phone" type="tel" form={form} onField={onField} />
            <Field label="Email Address" k="email" type="email" form={form} onField={onField} />
            <Field label="Customer ID / Application No." k="customer_id" form={form} onField={onField} />
            <Field label="Branch Name" k="branch" form={form} onField={onField} />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
                Complaint Category <span className="text-[#D4AF37]">*</span>
              </label>
              <select
                data-testid="grievance-field-category"
                required
                value={form.category || ""}
                onChange={(e) => onField("category", e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-[#0A1F44] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] transition"
              >
                <option value="">— Select —</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
                Complaint Details <span className="text-[#D4AF37]">*</span>
              </label>
              <textarea
                data-testid="grievance-field-details"
                rows={4}
                value={form.details || ""}
                onChange={(e) => onField("details", e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] transition resize-none"
                placeholder="Please describe the issue, dates, and any reference numbers."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
                Your Expected Resolution
              </label>
              <textarea
                data-testid="grievance-field-expected"
                rows={2}
                value={form.expected_resolution || ""}
                onChange={(e) => onField("expected_resolution", e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] transition resize-none"
              />
            </div>
          </div>

          {status.state === "error" && (
            <div data-testid="grievance-error" className="mt-5 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
              <AlertCircle size={14} /> {status.message}
            </div>
          )}
          {status.state === "success" && (
            <div data-testid="grievance-success" className="mt-5 px-4 py-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p>{status.message}</p>
                  {status.ticket && (
                    <p className="mt-2">
                      Ticket number: <span className="font-mono text-[#F0D788]">{status.ticket}</span> · Save this for follow-up.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="grievance-submit"
            disabled={status.state === "loading"}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold disabled:opacity-70"
          >
            <Send size={14} />
            {status.state === "loading" ? "Submitting…" : "Submit Complaint"}
          </button>

          {RECAPTCHA_ENABLED && (
            <p
              data-testid="grievance-recaptcha-notice"
              className="mt-3 text-[11px] text-white/45 flex items-start gap-1.5 leading-snug"
            >
              <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
              <span>
                Protected by reCAPTCHA — the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-[#F0D788]"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-[#F0D788]"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </span>
            </p>
          )}
        </form>

        {/* Escalation matrix */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl p-7 border border-[#D4AF37]/25 bg-white/[0.03]">
            <Shield size={22} className="text-[#D4AF37]" />
            <h3 className="mt-3 font-display text-xl">7-Level Escalation Hierarchy</h3>
            <p className="mt-1 text-white/55 text-xs leading-relaxed">
              If unresolved at any level, escalate to the next.
            </p>
            <ol className="mt-5 space-y-2.5">
              {ESCALATION.map((e) => (
                <li
                  key={e.level}
                  data-testid={`grievance-escalation-${e.level}`}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/5"
                >
                  <span className="w-7 h-7 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F0D788] text-xs font-semibold shrink-0">
                    L{e.level}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-white">{e.role}</div>
                    <div className="text-[11px] text-white/50">{e.contact}</div>
                  </div>
                  <ChevronRight size={14} className="text-white/30 ml-auto mt-1.5" />
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-3xl p-7 border border-[#D4AF37]/25 bg-white/[0.03]">
            <h3 className="font-display text-lg">Direct Contact</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href="tel:+917016975324" className="flex items-center gap-2 text-white/80 hover:text-[#F0D788]">
                  <Phone size={14} className="text-[#D4AF37]" /> 70169 75324
                </a>
              </li>
              <li>
                <a href="mailto:sahyogpfir@gmail.com" className="flex items-center gap-2 text-white/80 hover:text-[#F0D788]">
                  <Mail size={14} className="text-[#D4AF37]" /> sahyogpfir@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </PolicyPageShell>
  );
}

function Field({ label, k, type = "text", form, onField }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
        {label}
      </label>
      <input
        data-testid={`grievance-field-${k}`}
        type={type}
        value={form[k] || ""}
        onChange={(e) => onField(k, e.target.value)}
        className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] transition"
      />
    </div>
  );
}
